// helpers/date.ts
export type DateInput = Date | string | number | null | undefined;

/**
 * Cabo San Lucas. Mexico abolished DST in 2022, so this is permanently UTC-7.
 * Don't substitute America/Denver: it shifts an hour at US DST changes.
 */
export const BUSINESS_TZ = "America/Mazatlan";

/**
 * Current hour (0-23) in the given IANA time zone.
 */
export function getHourInTZ(tz: string = BUSINESS_TZ): number {
  return Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "2-digit",
      hour12: false,
    }).format(new Date())
  );
}

/**
 * "Today" in the given time zone, as a local-noon Date. Noon avoids the
 * DST/UTC off-by-one that midnight-anchored dates suffer from.
 */
export function getTodayInTZAsLocalNoon(tz: string = BUSINESS_TZ): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const y = Number(parts.find((p) => p.type === "year")?.value);
  const m = Number(parts.find((p) => p.type === "month")?.value);
  const d = Number(parts.find((p) => p.type === "day")?.value);

  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/**
 * The current wall-clock moment in the business time zone, expressed as a
 * Date in the *viewer's* local frame so it can be compared against the
 * local-frame Dates the pickers build from user selections.
 *
 * TimePicker previously hand-rolled this as `new Date()` with only setHours()
 * overridden from Cabo — keeping the browser's calendar date AND the browser's
 * minutes while swapping in Cabo's hour. That hybrid is wrong in two ways at
 * once: "is this the same day" was answered in the viewer's time zone, and the
 * minute comparison mixed frames. For any viewer whose local date had already
 * rolled past Cabo's (US Eastern after midnight, UK/Europe overnight) every
 * slot before Cabo's current hour was rejected as "already passed", silently
 * blocking same-day booking for that whole cohort.
 */
export function nowInBusinessTz(tz: string = BUSINESS_TZ): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);

  // hourCycle quirk: some engines format midnight as "24" with hour12:false.
  const hour = get("hour") % 24;

  return new Date(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    0,
    0
  );
}

export function toDateOnlyLocal(input: DateInput, noonHour = 12): Date | null {
  if (!input) return null;
  if (input instanceof Date && !isNaN(input.getTime()))
    return new Date(
      input.getFullYear(),
      input.getMonth(),
      input.getDate(),
      noonHour,
      0,
      0,
      0
    );

  if (typeof input === "string") {
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3], noonHour, 0, 0, 0);
    const d = new Date(input);
    return isNaN(d.getTime())
      ? null
      : new Date(d.getFullYear(), d.getMonth(), d.getDate(), noonHour, 0, 0, 0);
  }

  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime())
      ? null
      : new Date(d.getFullYear(), d.getMonth(), d.getDate(), noonHour, 0, 0, 0);
  }
  return null;
}

export function toISODateOnlyLocal(input: DateInput): string | null {
  const d = toDateOnlyLocal(input);
  if (!d) return null;
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
